import React, { useState } from 'react';
import { ZapIcon, ShieldCheckIcon, ArrowRightIcon, UserIcon, ChevronLeftIcon, CheckCircleIcon, MapPinIcon, CalendarIcon, MailIcon, PhoneIcon, FileTextIcon } from 'lucide-react';
type Step = 1 | 2 | 3;
type RoleChoice = 'PSW' | 'RPN' | 'RN' | null;
const ROLE_DETAILS = {
  PSW: {
    label: 'Personal Support Worker',
    desc: 'Daily living assistance, personal care, companionship',
    color: 'bg-accent-teal',
    hoverColor: 'hover:bg-accent-teal-dark',
    lightBg: 'bg-soft-teal',
    textColor: 'text-accent-teal',
    available: 8
  },
  RPN: {
    label: 'Registered Practical Nurse',
    desc: 'Medication management, assessments, direct patient care',
    color: 'bg-purple-600',
    hoverColor: 'hover:bg-purple-700',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-600',
    available: 4
  },
  RN: {
    label: 'Registered Nurse',
    desc: 'Complex care, acute settings, clinical leadership',
    color: 'bg-medical-blue',
    hoverColor: 'hover:bg-medical-blue-dark',
    lightBg: 'bg-blue-50',
    textColor: 'text-medical-blue',
    available: 2
  }
};
export function HeroSection() {
  const [step, setStep] = useState<Step>(1);
  const [role, setRole] = useState<RoleChoice>(null);
  const [postalCode, setPostalCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const handleRoleSelect = (r: RoleChoice) => {
    setRole(r);
    setStep(2);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (postalCode.length >= 3) {
      setSubmitted(true);
    }
  };
  const resetWidget = () => {
    setStep(1);
    setRole(null);
    setPostalCode('');
    setEmail('');
    setPhone('');
    setNotes('');
    setSubmitted(false);
  };
  return <section id="hero" className="w-full relative overflow-hidden" aria-label="Hero section" data-id="element-26">
      {/* Background video */}
      <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover z-0" aria-hidden="true" data-id="element-27">
        <source src="https://cdn.magicpatterns.com/uploads/wgsBFaTe36AcWvf398jgRE/6130562-hd_1080_1920_30fps.mp4" type="video/mp4" data-id="element-28" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-text-dark/70 z-10" aria-hidden="true" data-id="element-29" />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-30">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 py-10 lg:py-16 items-center" data-id="element-31">
          {/* Left: Headline */}
          <div className="flex flex-col justify-center" data-id="element-32">
            {/* Live indicator — hidden on mobile */}
            <div className="hidden sm:inline-flex items-center gap-2 w-fit mb-4" data-id="element-33">
              <span className="relative flex h-2.5 w-2.5" data-id="element-34">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-35" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" data-id="element-36" />
              </span>
              <span className="text-sm font-semibold text-green-300" data-id="element-37">
                14 Caregivers available in your area now.
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight mb-0 mt-0" data-id="element-38">
              Immediate <span className="text-[#5bb8f5]" data-id="element-39">Nurse-Led</span>{' '}
              Staffing
            </h1>

            <p className="text-base text-white/80 leading-relaxed mt-0 mb-2 sm:mb-6 max-w-md" data-id="element-40">
              Canada's first real-time healthcare staffing marketplace —
              connecting facilities with nurse-vetted PSWs, RPNs, and RNs in
              minutes.
            </p>

            {/* Trust signals — hidden on mobile */}
            <div className="hidden sm:flex flex-wrap gap-3 mb-6" data-id="element-41">
              <div className="flex items-center gap-1.5 text-sm text-white/80" data-id="element-42">
                <ShieldCheckIcon className="w-4 h-4 text-green-300 flex-shrink-0" data-id="element-43" />
                <span data-id="element-44">Nurse-Vetted</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-white/80" data-id="element-45">
                <ZapIcon className="w-4 h-4 text-[#5bb8f5] flex-shrink-0" data-id="element-46" />
                <span data-id="element-47">4-Min Match</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-white/80" data-id="element-48">
                <span className="text-base" data-id="element-49">🍁</span>
                <span data-id="element-50">Proudly Canadian</span>
              </div>
            </div>

            {/* Stats — hidden on mobile */}
            <div className="hidden sm:grid grid-cols-3 gap-4 pt-5 border-t border-white/20" data-id="element-51">
              <div data-id="element-52">
                <div className="text-2xl font-extrabold text-white" data-id="element-53">2,400+</div>
                <div className="text-xs text-white/60 mt-0.5 font-medium" data-id="element-54">
                  Verified Workers
                </div>
              </div>
              <div data-id="element-55">
                <div className="text-2xl font-extrabold text-white" data-id="element-56">98%</div>
                <div className="text-xs text-white/60 mt-0.5 font-medium" data-id="element-57">
                  Fill Rate
                </div>
              </div>
              <div data-id="element-58">
                <div className="text-2xl font-extrabold text-white" data-id="element-59">4 min</div>
                <div className="text-xs text-white/60 mt-0.5 font-medium" data-id="element-60">
                  Avg. Match
                </div>
              </div>
            </div>
          </div>

          {/* Right: Multi-step widget */}
          <div className="flex items-center justify-center lg:justify-end" data-id="element-61">
            <div className="w-full max-w-sm" data-id="element-62">
              <div className="bg-white rounded-2xl shadow-float border border-gray-100 overflow-hidden" data-id="element-63">
                {/* Widget header */}
                <div className="bg-medical-blue px-5 py-4" data-id="element-64">
                  <div className="flex items-center justify-between" data-id="element-65">
                    <div data-id="element-66">
                      <h2 className="text-white font-bold text-base leading-tight" data-id="element-67">
                        Find Care Now
                      </h2>
                      <p className="text-white/70 text-xs mt-0.5" data-id="element-68">
                        {step === 1 && 'Select the role you need'}
                        {step === 2 && 'Enter your location'}
                        {step === 3 && 'Confirm your request'}
                      </p>
                    </div>
                    {/* Step indicator */}
                    <div className="flex items-center gap-1" data-id="element-69">
                      {[1, 2, 3].map(s => <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? 'w-6 bg-white' : s < step ? 'w-3 bg-white/60' : 'w-3 bg-white/30'}`} data-id="element-70" />)}
                    </div>
                  </div>
                </div>

                {/* Step 1: Role selection */}
                {step === 1 && !submitted && <div className="p-5 space-y-3" data-id="element-71">
                    <button onClick={() => handleRoleSelect('PSW')} className="w-full flex items-center gap-4 bg-soft-teal hover:bg-[#c8e9e7] border-2 border-transparent hover:border-accent-teal text-left px-4 py-4 rounded-xl transition-all group" aria-label="Hire a Personal Support Worker" data-id="element-72">
                      <div className="w-11 h-11 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-73">
                        <UserIcon className="w-5 h-5 text-white" data-id="element-74" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-75">
                        <div className="font-bold text-text-dark text-sm" data-id="element-76">
                          Hire a PSW
                        </div>
                        <div className="text-xs text-text-muted mt-0.5" data-id="element-77">
                          Personal Support Worker
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-78">
                        <div className="text-xs font-bold text-accent-teal" data-id="element-79">
                          8 available
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-accent-teal ml-auto mt-0.5 group-hover:translate-x-0.5 transition-transform" data-id="element-80" />
                      </div>
                    </button>

                    <button onClick={() => handleRoleSelect('RPN')} className="w-full flex items-center gap-4 bg-purple-50 hover:bg-purple-100 border-2 border-transparent hover:border-purple-400 text-left px-4 py-4 rounded-xl transition-all group" aria-label="Hire a Registered Practical Nurse" data-id="element-81">
                      <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-82">
                        <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-83" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-84">
                        <div className="font-bold text-text-dark text-sm" data-id="element-85">
                          Hire an RPN
                        </div>
                        <div className="text-xs text-text-muted mt-0.5" data-id="element-86">
                          Registered Practical Nurse
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-87">
                        <div className="text-xs font-bold text-purple-600" data-id="element-88">
                          4 available
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-purple-600 ml-auto mt-0.5 group-hover:translate-x-0.5 transition-transform" data-id="element-89" />
                      </div>
                    </button>

                    <button onClick={() => handleRoleSelect('RN')} className="w-full flex items-center gap-4 bg-blue-50 hover:bg-blue-100 border-2 border-transparent hover:border-medical-blue text-left px-4 py-4 rounded-xl transition-all group" aria-label="Hire a Registered Nurse" data-id="element-90">
                      <div className="w-11 h-11 bg-medical-blue rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-91">
                        <ZapIcon className="w-5 h-5 text-white" data-id="element-92" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-93">
                        <div className="font-bold text-text-dark text-sm" data-id="element-94">
                          Hire an RN
                        </div>
                        <div className="text-xs text-text-muted mt-0.5" data-id="element-95">
                          Registered Nurse
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-96">
                        <div className="text-xs font-bold text-medical-blue" data-id="element-97">
                          2 available
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-medical-blue ml-auto mt-0.5 group-hover:translate-x-0.5 transition-transform" data-id="element-98" />
                      </div>
                    </button>
                  </div>}

                {/* Step 2: Location + date + contact */}
                {step === 2 && !submitted && role && <form onSubmit={e => {
                e.preventDefault();
                setStep(3);
              }} className="p-5 space-y-4" data-id="element-99">
                    {/* Selected role chip */}
                    <div className="flex items-center gap-2" data-id="element-100">
                      <button type="button" onClick={() => setStep(1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Go back" data-id="element-101">
                        <ChevronLeftIcon className="w-4 h-4 text-text-muted" data-id="element-102" />
                      </button>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${ROLE_DETAILS[role].color} text-white`} data-id="element-103">
                        {role} — {ROLE_DETAILS[role].label}
                      </span>
                    </div>

                    <div data-id="element-104">
                      <label htmlFor="postal-code" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-105">
                        <MapPinIcon className="w-3.5 h-3.5 inline mr-1 text-text-muted" data-id="element-106" />
                        Postal Code
                      </label>
                      <input id="postal-code" type="text" placeholder="e.g. M5V 3A8" value={postalCode} onChange={e => setPostalCode(e.target.value.toUpperCase())} maxLength={7} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-medical-blue focus:outline-none text-text-dark placeholder-gray-400 font-medium transition-colors text-sm" aria-label="Enter your postal code" autoFocus data-id="element-107" />
                    </div>

                    <div data-id="element-108">
                      <label htmlFor="shift-date" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-109">
                        <CalendarIcon className="w-3.5 h-3.5 inline mr-1 text-text-muted" data-id="element-110" />
                        Shift Date
                      </label>
                      <input id="shift-date" type="date" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-medical-blue focus:outline-none text-text-dark font-medium transition-colors text-sm" aria-label="Select shift date" data-id="element-111" />
                    </div>

                    <div data-id="element-112">
                      <label htmlFor="contact-email" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-113">
                        <MailIcon className="w-3.5 h-3.5 inline mr-1 text-text-muted" data-id="element-114" />
                        Email Address
                      </label>
                      <input id="contact-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-medical-blue focus:outline-none text-text-dark placeholder-gray-400 font-medium transition-colors text-sm" aria-label="Enter your email address" data-id="element-115" />
                    </div>

                    <div data-id="element-116">
                      <label htmlFor="contact-phone" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-117">
                        <PhoneIcon className="w-3.5 h-3.5 inline mr-1 text-text-muted" data-id="element-118" />
                        Phone Number
                      </label>
                      <input id="contact-phone" type="tel" placeholder="e.g. (416) 555-0123" value={phone} onChange={e => setPhone(e.target.value)} required className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-medical-blue focus:outline-none text-text-dark placeholder-gray-400 font-medium transition-colors text-sm" aria-label="Enter your phone number" data-id="element-119" />
                    </div>

                    <button type="submit" className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-120">
                      <ZapIcon className="w-4 h-4" data-id="element-121" />
                      Find Available {role}s
                      <ArrowRightIcon className="w-4 h-4" data-id="element-122" />
                    </button>
                  </form>}

                {/* Step 3: Confirm + notes */}
                {step === 3 && !submitted && role && <form onSubmit={handleSubmit} className="p-5 space-y-4" data-id="element-123">
                    <div className="flex items-center gap-2 mb-1" data-id="element-124">
                      <button type="button" onClick={() => setStep(2)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Go back" data-id="element-125">
                        <ChevronLeftIcon className="w-4 h-4 text-text-muted" data-id="element-126" />
                      </button>
                      <span className="text-sm font-semibold text-text-dark" data-id="element-127">
                        Confirm your request
                      </span>
                    </div>

                    {/* Summary card */}
                    <div className="bg-soft-teal rounded-xl p-4 space-y-2" data-id="element-128">
                      <div className="flex justify-between text-sm" data-id="element-129">
                        <span className="text-text-muted font-medium" data-id="element-130">
                          Role
                        </span>
                        <span className="font-bold text-text-dark" data-id="element-131">
                          {role} — {ROLE_DETAILS[role].label}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-132">
                        <span className="text-text-muted font-medium" data-id="element-133">
                          Location
                        </span>
                        <span className="font-bold text-text-dark" data-id="element-134">
                          {postalCode || 'Not specified'}
                        </span>
                      </div>
                      {email && <div className="flex justify-between text-sm" data-id="element-135">
                          <span className="text-text-muted font-medium" data-id="element-136">
                            Email
                          </span>
                          <span className="font-bold text-text-dark truncate max-w-[160px]" data-id="element-137">
                            {email}
                          </span>
                        </div>}
                      {phone && <div className="flex justify-between text-sm" data-id="element-138">
                          <span className="text-text-muted font-medium" data-id="element-139">
                            Phone
                          </span>
                          <span className="font-bold text-text-dark" data-id="element-140">
                            {phone}
                          </span>
                        </div>}
                      <div className="flex justify-between text-sm" data-id="element-141">
                        <span className="text-text-muted font-medium" data-id="element-142">
                          Available
                        </span>
                        <span className="font-bold text-accent-teal" data-id="element-143">
                          {ROLE_DETAILS[role].available} nearby
                        </span>
                      </div>
                    </div>

                    {/* Notes / special requests */}
                    <div data-id="element-144">
                      <label htmlFor="special-notes" className="block text-sm font-semibold text-text-dark mb-1.5" data-id="element-145">
                        <FileTextIcon className="w-3.5 h-3.5 inline mr-1 text-text-muted" data-id="element-146" />
                        Special Requests / Notes
                        <span className="text-text-muted font-normal ml-1" data-id="element-147">
                          (optional)
                        </span>
                      </label>
                      <textarea id="special-notes" rows={3} placeholder="e.g. Bilingual French/English preferred, experience with dementia care, overnight shift..." value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-medical-blue focus:outline-none text-text-dark placeholder-gray-400 font-medium transition-colors text-sm resize-none" aria-label="Special requests or notes" data-id="element-148" />
                    </div>

                    <button type="submit" className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-149">
                      <ZapIcon className="w-4 h-4" data-id="element-150" />
                      Request Instant Interview
                    </button>
                    <p className="text-center text-xs text-text-muted" data-id="element-151">
                      No commitment · Free for facilities
                    </p>
                  </form>}

                {/* Success state */}
                {submitted && <div className="p-6 text-center" data-id="element-152">
                    <div className="w-14 h-14 bg-soft-teal rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-153">
                      <CheckCircleIcon className="w-7 h-7 text-accent-teal" data-id="element-154" />
                    </div>
                    <h3 className="font-bold text-text-dark text-base mb-1" data-id="element-155">
                      Request Sent!
                    </h3>
                    <p className="text-text-muted text-sm mb-4" data-id="element-156">
                      We'll match you with a {role} and call within 4 minutes.
                    </p>
                    <button onClick={resetWidget} className="text-medical-blue font-semibold text-sm hover:underline" data-id="element-157">
                      Start a new search
                    </button>
                  </div>}

                {/* Bottom trust bar */}
                <div className="bg-soft-teal px-5 py-2.5 flex items-center justify-between" data-id="element-158">
                  <span className="text-xs text-accent-teal-dark font-semibold flex items-center gap-1.5" data-id="element-159">
                    <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-160" />
                    Live matching active
                  </span>
                  <span className="text-xs text-text-muted" data-id="element-161">
                    Avg. 4 min response
                  </span>
                </div>
              </div>

              {/* Role pills */}
              <div className="mt-3 flex flex-wrap gap-1.5 justify-center" data-id="element-162">
                {['PSW', 'RPN', 'RN', 'LPN', 'HCA'].map(r => <span key={r} className="px-2.5 py-1 bg-white border border-gray-200 text-text-muted text-xs font-semibold rounded-full shadow-sm" data-id="element-163">
                    {r}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}